import { HOUR_IN_MILLISECONDS } from 'config';
import {
  InspectionPointDto,
  MineModelDto,
  PagedListOfNodeVolumeRecordDto,
  PagedListOfWearDataDto,
  PourTypeDataDto,
  ProjectDto,
  StopeBackfillSummaryDto,
  useGetInspectionPointsByMineModelIdQuery,
  useMineModelQuery,
  useMineOverviewQuery,
  usePourTypesQuery,
  useProjectQuery,
  useSearchNodeVolumeRecordsQuery,
  useSearchWearDataQuery
} from 'providers/api';
import { UseQueryResult } from 'react-query';
import { useScenario } from 'modules/project/providers/scenario';
import { ProjectAreaPageParams } from './constants';

export type ProjectAreaQueries = [
  UseQueryResult<ProjectDto>,
  UseQueryResult<MineModelDto | undefined>,
  UseQueryResult<StopeBackfillSummaryDto[]>,
  UseQueryResult<PourTypeDataDto[]>,
  UseQueryResult<InspectionPointDto[]>,
  UseQueryResult<PagedListOfWearDataDto>,
  UseQueryResult<PagedListOfNodeVolumeRecordDto>,
];

const useProjectAreaQueries = (params: ProjectAreaPageParams): ProjectAreaQueries => {
  const { activeScenarioId, isResolvingScenario } = useScenario();
  const scenarioId = activeScenarioId ?? undefined;

  const projectQuery = useProjectQuery(params.entityId, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const mineModelQuery = useMineModelQuery({ mineModelId: params.entityId, scenarioId }, {
    staleTime: HOUR_IN_MILLISECONDS,
    keepPreviousData: true,
    enabled: !isResolvingScenario,
  });

  const mineOverviewQuery = useMineOverviewQuery({ mineModelId: params.entityId, scenarioId }, {
    staleTime: HOUR_IN_MILLISECONDS,
    enabled: !isResolvingScenario,
  });

  const inspectionPointsQuery = useGetInspectionPointsByMineModelIdQuery(params.entityId, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const minePourTypesQuery = usePourTypesQuery(params.entityId);

  const wearDataQuery = useSearchWearDataQuery({
    mineModelId: params.entityId,
    page: 1,
    pageSize: 1000,
    inspectionPoint: '',
  }, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  const nodeVolumeRecordsQuery = useSearchNodeVolumeRecordsQuery({
    mineModelId: params.entityId,
    page: 1,
    pageSize: 1000,
  }, {
    staleTime: HOUR_IN_MILLISECONDS,
  });

  return [projectQuery, mineModelQuery, mineOverviewQuery, minePourTypesQuery, inspectionPointsQuery, wearDataQuery, nodeVolumeRecordsQuery];
};

export default useProjectAreaQueries;
